import { inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Maybe } from '@orderna/admin-panel/src/types/maybe';
import { QueryOptions } from '@orderna/admin-panel/src/types/query-options';
import { Data } from '@orderna/admin-panel/src/utils/service';
import { BehaviorSubject, delay, Observable } from 'rxjs';
import { TRANSACTION_ITEMS } from '../../../../data/transaction-items';
import { TRANSACTIONS } from '../../../../data/transactions';
import { DailyTransactions } from '../../../../model/daily-transactions';
import { TransactionItem } from '../../../../model/transaction-item';
import { IDailyTransactionsApiService } from './daily-transactions-api.interface';

@Injectable({
  providedIn: 'root',
})
export class DailyTransactionsMockApiService implements IDailyTransactionsApiService {
  private transactionItems: TransactionItem[] = [...TRANSACTION_ITEMS];
  private dateAdapter = inject(DateAdapter<Date>);

  data = {
    daily_transactions: {
      items: [] as DailyTransactions[],
      queryOptions: { page: 1, size: 10 } as QueryOptions<DailyTransactions>,
      subject: new BehaviorSubject<Maybe<DailyTransactions[]>>(null),
      totalSubject: new BehaviorSubject<Maybe<number>>(null),
      totalAfterFilterSubject: new BehaviorSubject<Maybe<number>>(null),
    },
  };
  get dailyTransactionsData() {
    return this.data['daily_transactions'] as Data<DailyTransactions, DailyTransactions>;
  }

  getDailyTransactions(
    options: QueryOptions<DailyTransactions>
  ): Observable<any> {
    let startDate: Date = new Date();
    let endDate: Date = new Date();

    startDate.setHours(0, 0, 0, 0);

    this.dailyTransactionsData.queryOptions = options ?? { page: 1, size: 10 };

    if (options.dateFilter) {
      const [start, end] = options.dateFilter.split('_');
      startDate = new Date(start);
      endDate = new Date(end);
    }

    const storeFilter = options.filters?.find((f) => f.field === 'store');

    const data: DailyTransactions[] = [];

    const filteredTransactions = TRANSACTIONS.filter(
      (t) =>
        // Filter transactions within 'startDate' to 'endDate'
        t.transactionTimestamp >= startDate &&
        t.transactionTimestamp <= endDate &&
        // Filter transactions by store
        !!storeFilter &&
        storeFilter?.value.includes(`${t.store.id}`)
    );
    const filteredTransactionIds = filteredTransactions.map((t) => t.id);
    const filteredTransactionItems = this.transactionItems.filter((t) =>
      filteredTransactionIds.includes(t.transactionId)
    );

    let count = 1;

    // Compute revenue, cost, fees, etc. data for each day from 'startDate' to 'endDate'
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      // Get transactions on current date
      const currentTransactions = filteredTransactions.filter(
        (t) => this.dateAdapter.compareDate(t.transactionTimestamp, startDate) === 0
      );
      const currentTransactionIds = currentTransactions.map((t) => t.id);
      const currentTransactionItems = filteredTransactionItems.filter((t) =>
        currentTransactionIds.includes(t.transactionId)
      );

      const revenue = currentTransactionItems
        .map((t) => t.product.price * t.quantity)
        .reduce((acc, value) => acc + value, 0);

      const cost = currentTransactionItems
        .map((t) => t.product.cost * t.quantity)
        .reduce((acc, value) => acc + value, 0);

      const serviceFees = currentTransactions
        .map((t) => t.serviceChargeAmount)
        .reduce((acc, value) => acc + value, 0);

      const tipsCount = currentTransactions.filter((t) => t.tip ?? 0 > 0).length;
      const tipsAmount = currentTransactions
        .filter((t) => t.tip ?? 0 > 0)
        .map((t) => t.tip ?? 0)
        .reduce((acc, value) => acc + value, 0);

      const refundsCount = currentTransactionItems.filter((t) => t.isRefund).length;

      const refundsAmount = currentTransactionItems
        .filter((t) => t.isRefund)
        .map((t) => t.product.price * t.quantity)
        .reduce((acc, value) => acc + value, 0);

      data.push({
        id: count,
        date: currentDate.toISOString(),
        transactionsCount: currentTransactions.length,
        revenue,
        costOfGoods: cost,
        grossProfit: revenue - cost,
        serviceFees,
        tipsCount,
        tipsAmount,
        refundsCount,
        refundsAmount,
      });
      count++;
      currentDate = this.dateAdapter.addCalendarDays(currentDate, 1);
    }

    const { page, size, sort } = this.dailyTransactionsData.queryOptions;

    // Apply sorting if provided
    if (sort) {
      const sortFactor = sort.direction === 'asc' ? 1 : -1;

      data.sort((a, b) => {
        const aValue = a[sort.field as keyof DailyTransactions];
        const bValue = b[sort.field as keyof DailyTransactions];

        // If values are the same, return 0
        if (aValue === bValue) {
          return 0;
        }

        // If any value is undefined or null, push it to the end (or the beginning, based on sort direction)
        if (aValue == null) {
          return -sortFactor;
        }
        if (bValue == null) {
          return sortFactor;
        }

        // Handle different types: number, string
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortFactor * (aValue - bValue); // Sort by numerical comparison
        }

        // If types do not match, fall back to string comparison to avoid errors
        return sortFactor * aValue.toString().localeCompare(bValue.toString());
      });
    }

    const start = (page - 1) * size;
    const end = start + size;

    this.dailyTransactionsData.totalSubject.next(data.length);
    this.dailyTransactionsData.subject.next(data.slice(start, end));

    return this.dailyTransactionsData.subject.asObservable().pipe(delay(1000));
  }
}
